import Download from '@mui/icons-material/Download'
import Edit from '@mui/icons-material/Edit'
import {
  Button,
  CircularProgress,
  Dialog,
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  useTheme,
  alpha,
} from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { MOMENT_FORMATS, REPORT_DATE_TYPES, REPORT_DATE_TYPES_ARRAY } from 'src/consumer/constants'
import { isCommonResponseSuccessful } from 'src/mentor/helpers/utilityFunctions'
import { getMPDataWithParams } from 'src/mentor/services'
import ModernizedTableComponent from './components/ModernizedTableComponent'
import CumulativeBrandReports from './components/CumulativeBrandReports'
import CustomTimeRangeComponent from './components/CustomTimeRangeComponent'
import {
  fetchClinicBrandReportsForOwner,
  getCorrectReportDateStringForClinicLocationReports,
  getCumulativeClinicBrandReportFromClinicReports,
} from './utils'

export default function ModernizedClinicReportsPage({
  associatedClinicBrands,
  associatedClinics,
  loadingAssociationsData,
  hideMainHeading = false,
  // When provided, lock to a single clinic (brand + location): preselect/hide the brand selector,
  // scope report fetches to the location, and render only that location. Defaults preserve behavior.
  lockedClinicBrandId = null,
  clinicLocationIdFilter = null,
  hideBrandSelector = false,
  // When the stats page hoists a single shared period selector, it drives the range from here and the
  // page's own Day/Week/... + Custom chips are hidden.
  externalPeriod = null,
}) {
  const theme = useTheme()
  const isLockedToClinic = Boolean(lockedClinicBrandId)
  const reportLocationFilter = clinicLocationIdFilter ? [clinicLocationIdFilter] : []
  const [allClinicLocations, setAllClinicLocations] = useState([])
  const [allClinicBrands, setAllClinicBrands] = useState([])
  const [selectedClinicBrand, setSelectedClinicBrand] = useState(null)
  const [reports, setReports] = useState()
  const [reportType, setReportType] = useState(REPORT_DATE_TYPES.day)
  const [customStartTime, setCustomStartTime] = useState()
  const [customEndTime, setCustomEndTime] = useState()

  const [cumulatedClinicBrandReport, setCumulatedClinicBrandReport] = useState(null)
  const [allScreeners, setAllScreeners] = useState([])

  const [showCustomTimeRangeReports, setShowCustomTimeRangeReportsDialog] = useState(false)

  const [loading, setLoading] = useState({
    clinics: true,
    clinicBrands: true,
    reports: true,
  })

  const [hideReports, setHideReports] = useState(false)

  const downloadButtonsRef = useRef({})

  async function fetchReports(startTime, endTime) {
    return await fetchClinicBrandReportsForOwner({
      selectedClinicBrandId: selectedClinicBrand?.id,
      startTime,
      endTime,
      clinicLocationIdsFilter: reportLocationFilter,
    })
  }

  async function getAllScreenersInClinicBrand() {
    const mentorRespData = await getMPDataWithParams(
      '/api/secure/clinic-admin/mentor/screener/list/all',
      {
        clinicBrandId: selectedClinicBrand?.id,
      },
    )
    if (mentorRespData?.status === 200) {
      if (isCommonResponseSuccessful(mentorRespData?.data?.code)) {
        setAllScreeners(mentorRespData?.data?.data?.screeners)
      }
    }
  }

  async function fetchAllClinicsInClinicBrand() {
    setLoading((prev) => ({ ...prev, clinics: true }))
    let detailedClinics = associatedClinics?.filter(
      (clinic) => clinic?.clinicBrandId === selectedClinicBrand?.id,
    )
    if (clinicLocationIdFilter) {
      detailedClinics = detailedClinics?.filter((clinic) => clinic?.id === clinicLocationIdFilter)
    }
    setAllClinicLocations(detailedClinics)
    setLoading((prev) => ({ ...prev, clinics: false }))
  }

  async function fetchAllClinicBrands() {
    setLoading((prev) => ({ ...prev, clinicBrands: true }))
    setAllClinicBrands(associatedClinicBrands)
    setLoading((prev) => ({ ...prev, clinicBrands: false }))
  }

  async function submitTimeRange(stTime, enTime) {
    setCustomStartTime(stTime)
    setCustomEndTime(enTime)

    setLoading((prev) => ({
      ...prev,
      reports: true,
    }))
    setShowCustomTimeRangeReportsDialog(false)
    setReportType('custom')

    const startTime = dayjs.utc(stTime).format(MOMENT_FORMATS.javaInstantFormat)
    const endTime = dayjs.utc(enTime).format(MOMENT_FORMATS.javaInstantFormat)

    const reports = await fetchReports(startTime, endTime)

    const allReports = [{ startTime, endTime, ...(reports || {}) }]
    setReports(allReports)
    if (allReports?.some((report) => report?.axiosStatus === 403)) {
      toast.error('You are not authorized to view these reports')
      setHideReports(true)
      setLoading((prev) => ({
        ...prev,
        reports: false,
      }))
      return
    }

    setReports(allReports)

    const cumulatedClinicBrandReport = getCumulativeClinicBrandReportFromClinicReports(allReports)
    setCumulatedClinicBrandReport(cumulatedClinicBrandReport)

    setHideReports(false)
    setLoading((prev) => ({
      ...prev,
      reports: false,
    }))
  }

  async function fetchPrevious7YearsReport() {
    const today = dayjs().endOf('year').utc().format(MOMENT_FORMATS.javaInstantFormat)

    const reports = await Promise.all(
      Array.from({ length: 7 }).map(async (_, index) => {
        const endTime = dayjs
          .utc(today)
          .subtract(index, 'year')
          .format(MOMENT_FORMATS.javaInstantFormat)
        const startTime = dayjs
          .utc(today)
          .subtract(index + 1, 'year')
          .format(MOMENT_FORMATS.javaInstantFormat)

        const reports = await fetchReports(startTime, endTime)
        return { startTime, endTime, ...(reports || {}) }
      }),
    )
    return reports
  }

  async function fetchPrevious7MonthReport() {
    const today = dayjs().endOf('month').utc().format(MOMENT_FORMATS.javaInstantFormat)

    const reports = await Promise.all(
      Array.from({ length: 7 }).map(async (_, index) => {
        const endTime = dayjs
          .utc(today)
          .subtract(index, 'month')
          .format(MOMENT_FORMATS.javaInstantFormat)
        const startTime = dayjs
          .utc(today)
          .subtract(index + 1, 'month')
          .format(MOMENT_FORMATS.javaInstantFormat)

        const reports = await fetchReports(startTime, endTime)
        return { startTime, endTime, ...(reports || {}) }
      }),
    )
    return reports
  }

  async function fetchPrevious7WeeksReports() {
    const today = dayjs().endOf('week').utc().format(MOMENT_FORMATS.javaInstantFormat)
    const reports = await Promise.all(
      Array.from({ length: 7 }).map(async (_, index) => {
        const endTime = dayjs
          .utc(today)
          .subtract(index, 'week')
          .format(MOMENT_FORMATS.javaInstantFormat)
        const startTime = dayjs
          .utc(today)
          .subtract(index + 1, 'week')
          .format(MOMENT_FORMATS.javaInstantFormat)

        const reports = await fetchReports(startTime, endTime)
        return { startTime, endTime, ...(reports || {}) }
      }),
    )
    return reports
  }

  async function fetchPrevious7DaysReports() {
    const today = dayjs().endOf('day').utc().format(MOMENT_FORMATS.javaInstantFormat)
    const reports = await Promise.all(
      Array.from({ length: 7 }).map(async (_, index) => {
        const endTime = dayjs
          .utc(today)
          .subtract(index, 'days')
          .format(MOMENT_FORMATS.javaInstantFormat)
        const startTime = dayjs
          .utc(today)
          .subtract(index + 1, 'days')
          .format(MOMENT_FORMATS.javaInstantFormat)

        const reports = await fetchReports(startTime, endTime)
        return { startTime, endTime, ...(reports || {}) }
      }),
    )
    return reports
  }

  async function fetchReportsBasedOnReportType(repType) {
    setLoading((prev) => ({
      ...prev,
      reports: true,
    }))

    var allReports = null
    if (repType === REPORT_DATE_TYPES.day) {
      const reports = await fetchPrevious7DaysReports()
      allReports = reports
    }
    if (repType === REPORT_DATE_TYPES.week) {
      const reports = await fetchPrevious7WeeksReports()
      allReports = reports
    }
    if (repType === REPORT_DATE_TYPES.month) {
      const reports = await fetchPrevious7MonthReport()
      allReports = reports
    }
    if (repType === REPORT_DATE_TYPES.year) {
      const reports = await fetchPrevious7YearsReport()
      allReports = reports
    }

    if (allReports?.some((report) => report?.axiosStatus === 403)) {
      toast.error('You are not authorized to view these reports')
      setHideReports(true)
      setLoading((prev) => ({
        ...prev,
        reports: false,
      }))
      return
    }

    setReports(allReports)

    const cumulatedClinicBrandReport = getCumulativeClinicBrandReportFromClinicReports(allReports)
    setCumulatedClinicBrandReport(cumulatedClinicBrandReport)

    setHideReports(false)
    setLoading((prev) => ({
      ...prev,
      reports: false,
    }))
  }

  function getCorrectReportDateString(dayjsReportDate) {
    return getCorrectReportDateStringForClinicLocationReports({
      dayjsReportDate,
      reportType,
    })
  }

  function closeCustomTimeReports() {
    setShowCustomTimeRangeReportsDialog(false)
  }

  function showCustomTimeReports() {
    setShowCustomTimeRangeReportsDialog(true)
  }

  function downloadAllReports() {
    Object.values(downloadButtonsRef.current).forEach((btnRef) => {
      btnRef?.click()
    })
  }

  useEffect(() => {
    fetchAllClinicBrands()
    fetchAllClinicsInClinicBrand()
    if (associatedClinicBrands?.length > 0) {
      const lockedBrand = lockedClinicBrandId
        ? associatedClinicBrands.find((brand) => brand?.id === lockedClinicBrandId)
        : null
      setSelectedClinicBrand(lockedBrand || associatedClinicBrands[0])
    }
  }, [associatedClinicBrands, associatedClinics])

  useEffect(() => {
    if (selectedClinicBrand) {
      fetchAllClinicsInClinicBrand()
      getAllScreenersInClinicBrand()
    }
  }, [selectedClinicBrand])

  useEffect(() => {
    if (externalPeriod) return // driven by the shared selector effect below
    if (selectedClinicBrand) {
      if (reportType === 'custom') {
        submitTimeRange(customStartTime, customEndTime)
      } else {
        fetchReportsBasedOnReportType(reportType)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClinicBrand])

  // Driven by the hoisted shared period selector: fetch the whole selected range as one report.
  useEffect(() => {
    if (externalPeriod?.from && externalPeriod?.to && selectedClinicBrand) {
      submitTimeRange(`${externalPeriod.from}T00:00:00.000Z`, `${externalPeriod.to}T23:59:59.999Z`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalPeriod?.from, externalPeriod?.to, selectedClinicBrand])

  return (
    <Box sx={{ py: 3 }}>
      {!hideMainHeading && (
        <Typography variant="h4" fontWeight={700} sx={{ mb: 3, color: 'text.primary' }}>
          Reports
        </Typography>
      )}

      {!hideBrandSelector && (
      <Card
        sx={{
          mb: 3,
          p: 3,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          borderRadius: '12px',
        }}
      >
        <CustomSelect
          id="clinicBrand"
          label="Clinic Brand"
          value={selectedClinicBrand}
          options={allClinicBrands
            ?.filter((clinicBrand) => clinicBrand?.enabled)
            .sort((a, b) => (a?.title || a?.brandName)?.localeCompare(b?.title || b?.brandName))}
          isLoading={loading.clinicBrands}
          getOptionValue={(optn) => optn?.id}
          getOptionLabel={(optn) => optn?.title || optn?.brandName}
          onChangeEvent={(e) => {
            setSelectedClinicBrand(e.target.value)
          }}
          selectType={selectTypes.object}
        />
      </Card>
      )}

      {!!selectedClinicBrand && allClinicLocations.length > 0 && (
        <>
          {/* Time Period Selector — hidden when the stats page drives the range from its shared selector. */}
          <Box
            sx={{
              display: externalPeriod ? 'none' : 'flex',
              gap: 1.5,
              mb: 3,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            {REPORT_DATE_TYPES_ARRAY?.map((repType) => (
              <Chip
                key={repType?.id}
                label={`Last 7 ${repType.title}s`}
                onClick={() => {
                  setReportType(repType.id)
                  fetchReportsBasedOnReportType(repType?.id)
                }}
                sx={{
                  backgroundColor:
                    repType.id === reportType
                      ? alpha(theme.palette.primary.main, 0.2)
                      : 'background.paper',
                  color: repType.id === reportType ? 'primary.main' : 'text.secondary',
                  fontWeight: repType.id === reportType ? 600 : 400,
                  border: '1px solid',
                  borderColor: repType.id === reportType ? 'primary.main' : 'divider',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    borderColor: 'primary.main',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
            <Chip
              label="Custom Period"
              onClick={() => {
                showCustomTimeReports(true)
              }}
              icon={reportType === 'custom' ? <Edit /> : null}
              sx={{
                backgroundColor:
                  reportType === 'custom'
                    ? alpha(theme.palette.primary.main, 0.2)
                    : 'background.paper',
                color: reportType === 'custom' ? 'primary.main' : 'text.secondary',
                fontWeight: reportType === 'custom' ? 600 : 400,
                border: '1px solid',
                borderColor: reportType === 'custom' ? 'primary.main' : 'divider',
                cursor: 'pointer',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.1),
                  borderColor: 'primary.main',
                },
                transition: 'all 0.2s ease',
              }}
            />
          </Box>

          {loading?.reports ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress size={40} />
            </Box>
          ) : (
            <>
              {!hideReports && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                    <Button
                      variant="contained"
                      onClick={downloadAllReports}
                      endIcon={<Download />}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 3,
                        py: 1,
                        fontWeight: 600,
                      }}
                    >
                      Download All Reports
                    </Button>
                  </Box>

                  {/* Cumulative Brand Reports — hidden when locked to a single location (redundant) */}
                  {!isLockedToClinic && cumulatedClinicBrandReport && (
                    <ModernizedTableComponent
                      entityTitle={selectedClinicBrand?.title || selectedClinicBrand?.brandName}
                      reportType={reportType}
                      reports={cumulatedClinicBrandReport}
                      screeners={allScreeners}
                      allClinicLocations={allClinicLocations}
                      defaultExpanded
                      accordionOpenAlways={true}
                      downloadButtonRef={downloadButtonsRef}
                    />
                  )}

                  {/* Individual Location Reports */}
                  {allClinicLocations?.map((clinicLocation) => {
                    const locationReports = reports
                      ?.map((report) => {
                        const foundReport = report?.reports?.find(
                          (r) => r.clinicLocationId === clinicLocation?.id,
                        )
                        return foundReport
                          ? {
                              ...foundReport,
                              reportEndTime: report?.endTime,
                              reportStartTime: report?.startTime,
                            }
                          : null
                      })
                      .filter(Boolean)
                      .sort(
                        (a, b) =>
                          dayjs.utc(b?.reportEndTime) - dayjs.utc(a?.reportEndTime),
                      )

                    return (
                      <ModernizedTableComponent
                        key={clinicLocation.id}
                        entityTitle={
                          isLockedToClinic
                            ? 'Detailed report'
                            : `Location: ${clinicLocation?.nickName || clinicLocation?.title}`
                        }
                        reportType={reportType}
                        reports={locationReports}
                        screeners={allScreeners}
                        allClinicLocations={[]}
                        defaultExpanded={false}
                        accordionOpenAlways={false}
                        downloadButtonRef={downloadButtonsRef}
                      />
                    )
                  })}
                </Box>
              )}
            </>
          )}
        </>
      )}
      <Dialog open={showCustomTimeRangeReports} onClose={closeCustomTimeReports}>
        <CustomTimeRangeComponent
          submitTimeRange={submitTimeRange}
          closeDialog={closeCustomTimeReports}
        />
      </Dialog>
    </Box>
  )
}

